import * as React from 'react';
import { CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '../../lib/cn';
import { IconButton } from './icon-button';

export type ToastSeverity = 'success' | 'error' | 'info';

const ICONS: Record<ToastSeverity, React.ElementType> = {
    success: CheckCircle2,
    error: XCircle,
    info: Info,
};

const ICON_COLORS: Record<ToastSeverity, string> = {
    success: 'text-state-success',
    error: 'text-state-danger',
    info: 'text-state-info',
};

export interface ToastProps {
    severity: ToastSeverity;
    message: string;
    onDismiss: () => void;
}

/**
 * Toast — single dismissable status bubble.
 *
 * Owned by ToastProvider; consumers do not render this directly. Use the
 * imperative API at src/lib/toast.ts (toast.success / toast.error / toast.info)
 * which dispatches a Toast through the provider's queue.
 *
 * Accessibility:
 *   - This visible bubble is presentational; screen-reader announcement is
 *     handled by the PERSISTENT polite/assertive live regions in ToastProvider
 *     (a live region created together with its content is often not announced).
 *   - The dismiss control uses the IconButton primitive with ariaLabel and is
 *     a 44px tap target, consistent with the rest of the chrome.
 *
 * Motion:
 *   - The fade + slide-in animation (.animate-toast-in) is defined in index.css
 *     inside a prefers-reduced-motion:no-preference block, so reduced-motion
 *     users see the toast appear instantly.
 */
export const Toast: React.FC<ToastProps> = ({ severity, message, onDismiss }) => {
    const Icon = ICONS[severity];
    const iconColor = ICON_COLORS[severity];

    return (
        <div
            className={cn(
                'pointer-events-auto bg-surface-ink text-white px-6 py-3 rounded-lg shadow-lg',
                'flex items-center gap-3 max-w-[calc(100vw-2rem)]',
                'animate-toast-in',
            )}
        >
            <Icon size={18} aria-hidden="true" className={cn('shrink-0', iconColor)} />
            <span className="text-sm min-w-0">{message}</span>
            <IconButton
                variant="ghost"
                onClick={onDismiss}
                icon={<X size={18} />}
                ariaLabel="Dismiss"
                className="h-11 w-11 text-white/70 hover:text-white hover:bg-white/10 -mr-2"
            />
        </div>
    );
};
